import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import type { AgentCardProps } from './AgentCard';

interface AgentModalProps {
  agent: AgentCardProps;
  onClose: () => void;
  onBookmark: () => void;
  onUseAgent?: (agent: AgentCardProps, defaultUrl?: string) => Promise<void>;
}

const useStyles = makeStyles((theme: Theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  container: {
    background: theme.palette.background.default,
    borderRadius: 8,
    width: '100%',
    maxWidth: 800,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  headerWrapper: {
    position: 'sticky',
    top: 0,
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.border}`,
    zIndex: 2,
  },
  header: {
    padding: '24px 24px 0 24px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 24,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  closeButton: {
    fontSize: 24,
    padding: 4,
    marginTop: -8,
    marginRight: -4,
    lineHeight: 1,
    color: theme.palette.text.tertiary,
  },
  statusBadgeWrapper: {
    padding: '8px 24px 16px 24px',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 500,
    color: '#1a1a1a',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  content: {
    padding: 24,
  },
  section: {
    marginBottom: 16,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '16px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  sectionChevron: {
    fontSize: 20,
    lineHeight: 1,
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  sectionContent: {
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s ease, padding 0.3s ease',
    padding: '0 12px',
  },
  sectionContentOpen: {
    maxHeight: 1000,
    padding: '0 12px 16px 12px',
  },
  description: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  list: {
    margin: 0,
    paddingLeft: 20,
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    margin: '8px 0',
  },
  footer: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.border}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    position: 'sticky',
    bottom: 0,
    background: theme.palette.background.paper,
    zIndex: 2,
  },
  bookmarkButton: {
    borderRadius: 4,
    padding: '6px 10px',
    height: 32,
    minWidth: 82,
    fontSize: 14,
    border: `1px solid ${theme.palette.border}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    textTransform: 'none',
  },
  closeActionButton: {
    borderRadius: 4,
    padding: '6px 10px',
    height: 32,
    fontSize: 14,
    border: 'none',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    '&:hover': {
      background: theme.palette.primary.dark || theme.palette.primary.main,
    },
  },
}));

const getApprovalColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.successBackground;
    case 'Under Review':
      return theme.palette.warningBackground;
    default:
      return theme.palette.background.secondary;
  }
};

const getApprovalTextColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.text.primary;
    case 'Under Review':
      return theme.palette.text.primary;
    default:
      return theme.palette.text.secondary;
  }
};

const getApprovalDotColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.success.main;
    case 'Under Review':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.tertiary;
  }
};

export const AgentModal: React.FC<AgentModalProps> = ({
  agent,
  onClose,
  onBookmark,
  onUseAgent,
}) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const targetAudience = agent.modalContent?.targetAudience;
  const governance = agent.modalContent?.governance;
  const integrationMethod = agent.modalContent?.integrationMethod;
  const agentConfiguration = agent.modalContent?.agentConfiguration;
  const interactionSettings = agent.modalContent?.interactionSettings;
  const useAssistantUrl =
    agentConfiguration?.agentUrl ||
    integrationMethod?.configuration ||
    undefined;
  const [isLaunching, setIsLaunching] = useState(false);

  const handleUseAssistant = async () => {
    if (onUseAgent) {
      setIsLaunching(true);
      try {
        await onUseAgent(agent, useAssistantUrl);
      } finally {
        setIsLaunching(false);
      }
      return;
    }

    if (useAssistantUrl) {
      window.open(useAssistantUrl, '_blank', 'noopener,noreferrer');
    }
  };
  const releaseNotes = agent.modalContent?.releaseNotes || [];
  const [openSections, setOpenSections] = useState({
    overview: true,
    capabilities: true,
    targetAudience: true,
    provideGet: true,
    trustGovernance: true,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Box className={classes.overlay} onClick={handleOverlayClick}>
      <Box className={classes.container}>
        <Box className={classes.headerWrapper}>
          <Box className={classes.header}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
              <Typography className={classes.title} component="h1">
                {agent.title}
              </Typography>
              {(useAssistantUrl || onUseAgent) && (
                <Button
                  className={classes.closeActionButton}
                  onClick={handleUseAssistant}
                  disabled={isLaunching}
                >
                  Use Agent
                </Button>
              )}
            </Box>
            <IconButton className={classes.closeButton} onClick={onClose} aria-label="Close">
              ×
            </IconButton>
          </Box>
          <Box className={classes.statusBadgeWrapper}>
            <Box className={classes.statusBadge} style={{ backgroundColor: getApprovalColor(agent.approvalStatus, theme), color: getApprovalTextColor(agent.approvalStatus, theme) }}>
              <Box className={classes.statusDot} style={{ backgroundColor: getApprovalDotColor(agent.approvalStatus, theme) }} />
              {agent.approvalStatus}
            </Box>
          </Box>
        </Box>

        <Box className={classes.content}>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('overview')}>
              <Typography className={classes.sectionTitle} component="h2">
                Overview
              </Typography>
              <Typography className={classes.sectionChevron}>{openSections.overview ? '^' : '>'}</Typography>
            </Box>
            <Box
              className={`${classes.sectionContent} ${openSections.overview ? classes.sectionContentOpen : ''}`}
            >
              {(agent.modalContent?.overview || []).length ? (
                agent.modalContent?.overview.map((paragraph, index) => (
                  <Typography key={index} className={classes.description} paragraph>
                    {paragraph}
                  </Typography>
                ))
              ) : (
                <Typography className={classes.description}>No overview provided.</Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('capabilities')}>
              <Typography className={classes.sectionTitle} component="h2">
                Capabilities
              </Typography>
              <Typography className={classes.sectionChevron}>{openSections.capabilities ? '^' : '>'}</Typography>
            </Box>
            <Box
              className={`${classes.sectionContent} ${openSections.capabilities ? classes.sectionContentOpen : ''}`}
            >
              {(agent.modalContent?.capabilities || []).length ? (
                <ul className={classes.list}>
                  {agent.modalContent?.capabilities.map((capability, index) => (
                    <li key={index}>{capability}</li>
                  ))}
                </ul>
              ) : (
                <Typography className={classes.description}>No capabilities provided.</Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('targetAudience')}>
              <Typography className={classes.sectionTitle} component="h2">
                Target Audience
              </Typography>
              <Typography className={classes.sectionChevron}>{openSections.targetAudience ? '^' : '>'}</Typography>
            </Box>
            <Box
              className={`${classes.sectionContent} ${openSections.targetAudience ? classes.sectionContentOpen : ''}`}
            >
              <Typography className={classes.subsectionTitle} component="h3">
                Designed For
              </Typography>
              <Typography className={classes.description}>
                {targetAudience?.designedFor || 'Not provided'}
              </Typography>
              <Typography className={classes.subsectionTitle} component="h3">
                Business Group
              </Typography>
              <Typography className={classes.description}>
                {targetAudience?.businessGroup || 'Not provided'}
              </Typography>
              <Typography className={classes.subsectionTitle} component="h3">
                Geography
              </Typography>
              <Typography className={classes.description}>
                {targetAudience?.geography || 'Not provided'}
              </Typography>
              {targetAudience?.geographicDetails && (
                <>
                  <Typography className={classes.subsectionTitle} component="h3">
                    Specific Location
                  </Typography>
                  <Typography className={classes.description}>
                    {targetAudience.geographicDetails}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {(integrationMethod?.method || integrationMethod?.configuration || agentConfiguration?.agentUrl || agentConfiguration?.agentVersion || (agentConfiguration?.skills || []).length || (agentConfiguration?.systemCapabilities || []).length || interactionSettings?.inputFormat || interactionSettings?.responseFormat || interactionSettings?.requiresSecureAccess) && (
            <Box className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle} component="h2">
                  Agent Configuration (By Agent Type)
                </Typography>
              </Box>
              <Box className={`${classes.sectionContent} ${classes.sectionContentOpen}`}>
                {integrationMethod?.method && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Integration Method</Typography>
                    <Typography className={classes.description}>{integrationMethod.method}</Typography>
                  </>
                )}
                {integrationMethod?.configuration && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Integration Configuration</Typography>
                    <Typography className={classes.description}>{integrationMethod.configuration}</Typography>
                  </>
                )}
                {agentConfiguration?.agentUrl && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Agent URL</Typography>
                    <Typography className={classes.description}>{agentConfiguration.agentUrl}</Typography>
                  </>
                )}
                {agentConfiguration?.agentVersion && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Agent Version</Typography>
                    <Typography className={classes.description}>{agentConfiguration.agentVersion}</Typography>
                  </>
                )}
                {!!(agentConfiguration?.skills || []).length && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Skills</Typography>
                    <ul className={classes.list}>
                      {(agentConfiguration?.skills || []).map((skill, index) => (
                        <li key={`skill-${index}`}>{skill}</li>
                      ))}
                    </ul>
                  </>
                )}
                {!!(agentConfiguration?.systemCapabilities || []).length && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">System Capabilities</Typography>
                    <ul className={classes.list}>
                      {(agentConfiguration?.systemCapabilities || []).map((capability, index) => (
                        <li key={`capability-${index}`}>{capability}</li>
                      ))}
                    </ul>
                  </>
                )}
                {interactionSettings?.inputFormat && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Input Format</Typography>
                    <Typography className={classes.description}>{interactionSettings.inputFormat}</Typography>
                  </>
                )}
                {interactionSettings?.responseFormat && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Response Format</Typography>
                    <Typography className={classes.description}>{interactionSettings.responseFormat}</Typography>
                  </>
                )}
                {typeof interactionSettings?.requiresSecureAccess === 'boolean' && (
                  <>
                    <Typography className={classes.subsectionTitle} component="h3">Requires Secure Access</Typography>
                    <Typography className={classes.description}>{interactionSettings.requiresSecureAccess ? 'Yes' : 'No'}</Typography>
                  </>
                )}
              </Box>
            </Box>
          )}

          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('provideGet')}>
              <Typography className={classes.sectionTitle} component="h2">
                What you Provide & What you get
              </Typography>
              <Typography className={classes.sectionChevron}>{openSections.provideGet ? '^' : '>'}</Typography>
            </Box>
            <Box
              className={`${classes.sectionContent} ${openSections.provideGet ? classes.sectionContentOpen : ''}`}
            >
              <Box>
                <Typography className={classes.subsectionTitle} component="h3">
                  You Provide:
                </Typography>
                {(agent.modalContent?.whatYouProvide || []).length ? (
                  <ul className={classes.list}>
                    {agent.modalContent?.whatYouProvide.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <Typography className={classes.description}>No input details provided.</Typography>
                )}
              </Box>
              <Box>
                <Typography className={classes.subsectionTitle} component="h3">
                  You get:
                </Typography>
                {(agent.modalContent?.whatYouGet || []).length ? (
                  <ul className={classes.list}>
                    {agent.modalContent?.whatYouGet.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <Typography className={classes.description}>No output details provided.</Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box className={classes.section}>
            <Box className={classes.sectionHeader} onClick={() => toggleSection('trustGovernance')}>
              <Typography className={classes.sectionTitle} component="h2">
                Trust & Governance
              </Typography>
              <Typography className={classes.sectionChevron}>{openSections.trustGovernance ? '^' : '>'}</Typography>
            </Box>
            <Box
              className={`${classes.sectionContent} ${openSections.trustGovernance ? classes.sectionContentOpen : ''}`}
            >
              <Typography className={classes.subsectionTitle} component="h3">
                Data Access
              </Typography>
              <Typography className={classes.description}>
                {governance?.dataAccess || 'Not provided'}
              </Typography>
              <Typography className={classes.subsectionTitle} component="h3">
                Compliance
              </Typography>
              <Typography className={classes.description}>
                {governance?.compliance || 'Not provided'}
              </Typography>
              <Typography className={classes.subsectionTitle} component="h3">
                Validation Summary
              </Typography>
              <Typography className={classes.description}>
                {governance?.validationSummary || 'Not provided'}
              </Typography>
              {governance?.evidenceLink && (
                <>
                  <Typography className={classes.subsectionTitle} component="h3">
                    Evidence Link
                  </Typography>
                  <Typography className={classes.description}>{governance.evidenceLink}</Typography>
                </>
              )}
              {governance?.evidenceFileName && (
                <>
                  <Typography className={classes.subsectionTitle} component="h3">
                    Evidence File
                  </Typography>
                  <Typography className={classes.description}>{governance.evidenceFileName}</Typography>
                </>
              )}
              {governance?.importantToKnow && (
                <>
                  <Typography className={classes.subsectionTitle} component="h3">
                    Important to Know
                  </Typography>
                  <Typography className={classes.description}>
                    {governance.importantToKnow}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {!!releaseNotes.length && (
            <Box className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle} component="h2">
                  Release Notes
                </Typography>
              </Box>
              <Box className={`${classes.sectionContent} ${classes.sectionContentOpen}`}>
                {releaseNotes.map((note, noteIdx) => (
                  <Box key={`${note.version}-${noteIdx}`}>
                    <Typography className={classes.subsectionTitle} component="h3">
                      {note.version}
                    </Typography>
                    <ul className={classes.list}>
                      {note.notes.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button className={classes.bookmarkButton} onClick={onBookmark}>
            Bookmark
          </Button>
          {(useAssistantUrl || onUseAgent) && (
            <Button
              className={classes.closeActionButton}
              onClick={handleUseAssistant}
              disabled={isLaunching}
            >
              Use Agent
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
